/**
 * discrepancyDisplay — canonical render helpers + lookup maps for the
 * Discrepancies UI (used by `ResourcePanel.js` and `CheckInScreen.js`).
 *
 * Extracted (Rem22 Sub 5 + Sub 11) so CheckIn copies from the canonical
 * source instead of inventing parallel implementations. Brainstormer
 * `MsgId:a254627` pinpointed ResourcePanel as the design-of-record:
 * Tabs config, Table layout, person resolver, date formatter, status /
 * priority maps all live here now and are imported by both consumers.
 *
 * Field-source priority follows the legacy `calendar.event-discrepancies.js`
 * + Symfony `DiscrepancyDto` shapes — pre-existing in `ResourcePanel.js:19-99`
 * before this extraction.
 *
 * @module discrepancyDisplay
 */

import { getUserFormat } from '../../common/user-formats/userFormats.js';

export const DISC_STATUS_CONFIG = {
	new:        { label: 'New',         color: 'secondary' },
	open:       { label: 'Open',        color: 'primary' },
	inprogress: { label: 'In Progress', color: 'warning' },
	deferred:   { label: 'Deferred',    color: 'default' },
	closed:     { label: 'Closed',      color: 'success' }
};

// IMPORTANT: backend Symfony enum has 1 = High (most urgent), 3 = Low.
// CheckInScreen's earlier Rem22 Sub 4 implementation had this reversed —
// corrected here to match ResourcePanel.
export const DISC_PRIORITY = {
	1: 'High',
	2: 'Medium',
	3: 'Low'
};

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL  = ['January','February','March','April','May','June','July','August','September','October','November','December'];

/**
 * Calendar date/time components for `date` as seen in `timezone`, via
 * `Intl.DateTimeFormat.formatToParts` (DST-correct). Returns `null` when
 * `timezone` is falsy/unusable so callers fall back to local getters —
 * i.e. exactly the pre-existing (browser-local) behavior.
 */
function tzParts(date, timezone) {
	if (!timezone || typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function')
		return null;
	try {
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: timezone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			hour12: false
		}).formatToParts(date);
		const pick = type => {
			const found = parts.find(part => part.type === type);
			return found ? Number(found.value) : NaN;
		};
		const year = pick('year');
		const month = pick('month');
		const day = pick('day');
		const hour = pick('hour');
		const minute = pick('minute');
		if ([year, month, day, hour, minute].some(v => !Number.isFinite(v))) return null;
		// hour12:false renders midnight as "24" in some engines.
		return { year, month, day, hour: hour === 24 ? 0 : hour, minute };
	} catch {
		return null;
	}
}

/**
 * Format a date value (epoch seconds, ISO yyyy-MM-dd, or string-parseable)
 * using the user's preferred display format from `userSettings` (flat, or the
 * `/user/settings` payload with formats under `userSetting`). Falls back to the shared
 * user formats → `'MM/dd/yyyy'`.
 *
 * @param {?string} [timezone] IANA tz to render an EPOCH value in. Ignored
 *   for the ISO/string branches (already a calendar date). Omit to keep the
 *   pre-existing browser-local behavior byte-identical.
 */
export function formatDateValue(value, userSettings, timezone) {
	if (value == null || value === '') return '—';
	let date;
	let isEpoch = false;
	const n = Number(value);
	// Rem23 Sub 3 — accept epoch SECONDS (1e8 ≤ n < 1e12) OR epoch MILLIS
	// (n ≥ 1e12). Backend ships `date: 1776429578` as plain Unix seconds.
	if (Number.isFinite(n) && n >= 1e12) {
		date = new Date(n);
		isEpoch = true;
	} else if (Number.isFinite(n) && n > 100000000) {
		date = new Date(n * 1000);
		isEpoch = true;
	} else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
		const [y, m, d] = value.split('-').slice(0, 3).map(Number);
		date = new Date(y, m - 1, d);
	} else {
		date = new Date(value);
	}
	if (!date || isNaN(date.getTime())) return String(value);

	const parts = isEpoch ? tzParts(date, timezone) : null;
	const y = parts ? String(parts.year) : String(date.getFullYear());
	const mo = parts ? parts.month - 1 : date.getMonth();
	const d = parts ? parts.day : date.getDate();
	const fmt =
		userSettings?.date ||
		userSettings?.angularDateFormat ||
		userSettings?.userSetting?.angularDateFormat ||
		getUserFormat('angularDateFormat') ||
		'MM/dd/yyyy';

	return fmt
		.replace('yyyy', y)
		.replace('MMMM', MONTHS_FULL[mo])
		.replace('MMM', MONTHS_SHORT[mo])
		.replace('MM', String(mo + 1).padStart(2, '0'))
		.replace('dd', String(d).padStart(2, '0'))
		.replace('d', String(d));
}

/**
 * `formatDateValue` + HH:mm appended when the source carries a clock time.
 *
 * @param {?string} [timezone] see `formatDateValue`.
 */
export function formatDateWithTime(value, userSettings, timezone) {
	const dateStr = formatDateValue(value, userSettings, timezone);
	if (dateStr === '—') return '—';
	let date;
	let isEpoch = false;
	const n = Number(value);
	// Rem23 Sub 3 — accept epoch seconds OR millis OR ISO string.
	if (Number.isFinite(n) && n >= 1e12) {
		date = new Date(n);
		isEpoch = true;
	} else if (Number.isFinite(n) && n > 100000000) {
		date = new Date(n * 1000);
		isEpoch = true;
	} else {
		date = new Date(value);
	}
	if (!date || isNaN(date.getTime())) return dateStr;
	const parts = isEpoch ? tzParts(date, timezone) : null;
	const hh = parts ? String(parts.hour).padStart(2, '0') : String(date.getHours()).padStart(2, '0');
	const mm = parts
		? String(parts.minute).padStart(2, '0')
		: String(date.getMinutes()).padStart(2, '0');
	return `${dateStr} ${hh}:${mm}`;
}

/**
 * Normalize Symfony's nested `{id, name, surname}` User shape (or a flat
 * string) into `{id, fullName}`. Returns `null` for nullish input.
 */
export function normalizePerson(personOrFlat) {
	if (!personOrFlat) return null;
	if (typeof personOrFlat === 'object') {
		return {
			id: personOrFlat.id ?? null,
			fullName: `${personOrFlat.name ?? ''} ${personOrFlat.surname ?? ''}`.trim() || null,
		};
	}
	return { id: null, fullName: String(personOrFlat) };
}

/**
 * Render "Assigned To" cell for a discrepancy row. Mirrors ResourcePanel
 * field-source priority — prefer the nested `assignTo` user, else the
 * legacy flat `assignedName + assignedSurname` pair, else `'—'`.
 */
export function resolveAssignedTo(d) {
	if (!d) return '—';
	const fromObj = normalizePerson(d.assignTo)?.fullName;
	if (fromObj) return fromObj;
	if (d.assignedName) return `${d.assignedName} ${d.assignedSurname || ''}`.trim();
	return '—';
}

/**
 * Coerce a discrepancy's `assignTo` value to a numeric user id (null when
 * unset). Backend `SaveDiscrepancyRequestDto.assignTo` accepts either a
 * scalar id or `null`; nested User objects → unwrap to `id`.
 */
export function normalizeAssignToId(val) {
	if (val == null || val === '') return null;
	if (typeof val === 'object') return val.id != null ? parseInt(val.id, 10) || null : null;
	const n = parseInt(val, 10);
	return isNaN(n) ? null : n;
}

/**
 * Build the full PUT payload required by Symfony `SaveDiscrepancyRequestDto`
 * (`name`, `description`, `priority`, `assignTo`, `logbookEntry`). Used by
 * any inline-edit path that mutates a non-status field — partial PUTs are
 * rejected with 422 (Rem23 Sub 1 root cause).
 *
 * `override` lets callers pass a single changed field; the rest are
 * carried over from the current `discrepancy` snapshot. `null` is a valid
 * override value (e.g. clear assignee) — only `undefined` is treated as
 * "no override".
 *
 * Status is intentionally NOT in this payload — it has a dedicated PATCH
 * endpoint (`DiscrepanciesApi.changeStatus`).
 *
 * @param {Object} discrepancy
 * @param {Object} [override]
 * @returns {{ name, description, priority, assignTo, logbookEntry }}
 */
export function buildSaveDiscrepancyPayload(discrepancy, override = {}) {
	const get = (field, def = '') =>
		override[field] !== undefined ? override[field] : (discrepancy?.[field] ?? def);
	const rawPriority = override.priority !== undefined ? override.priority : (discrepancy?.priority ?? '');
	return {
		name:         get('name'),
		description:  get('description'),
		priority:     parseInt(rawPriority, 10) || null,
		assignTo:     normalizeAssignToId(
			override.assignTo !== undefined ? override.assignTo : discrepancy?.assignTo
		),
		logbookEntry: get('logbookEntry', '')
	};
}
